import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import styled from 'styled-components';

import { useUserSelector } from 'hooks/useUser';
import { usePassport } from 'hooks/usePassport';
import { useShouldExcludeSidebar } from 'hooks/useShouldExcludeSidebar';
import PersistentSidebar from '../PageWithSidebar';
import WiseClaimRewardPopup from '../WiseClaimRewardPopup';

const LOADING_STYLE = {
  width: '100%',
  padding: '20px',
  textAlign: 'center'
};

const Wrapper = styled.div`
  display: flex;
  margin-left: -12px;
  margin-right: -12px;
  margin-top: -8px;
`;

const Container = styled.div`
  width: calc(100% - 300px);
  margin-left: 300px;
  position: relative;

  @media (max-width: 874px) {
    margin-left: 0;
    width: 100%;
  }
`;

const IntroScreen = ({ children, requiredProfileCompleted, allowSkippedToBookCall }) => {
  const {
    isLoggedIn,
    isLoading,
    isOnboardingPersonalDataCompleted,
    isPropertyFinderUser,
    isCountryServicesUser
  } = useUserSelector();
  const { stage3Submitted, hasSkippedToBookCall, isPassportRequestsLoading } = usePassport();
  const { shouldExcludeSidebar, shouldBlurSidebar } = useShouldExcludeSidebar();

  // Check if we need to redirect to profile intro (must be before early returns)
  const shouldRedirectToIntro = useMemo(
    () =>
      (!isOnboardingPersonalDataCompleted || !stage3Submitted) &&
      !(allowSkippedToBookCall && hasSkippedToBookCall) &&
      !isPropertyFinderUser &&
      !isCountryServicesUser &&
      requiredProfileCompleted &&
      !isPassportRequestsLoading,
    [
      isOnboardingPersonalDataCompleted,
      stage3Submitted,
      allowSkippedToBookCall,
      hasSkippedToBookCall,
      isPropertyFinderUser,
      isCountryServicesUser,
      requiredProfileCompleted,
      isPassportRequestsLoading
    ]
  );

  if (!isLoggedIn && !isLoading) {
    return <Navigate to="/login" replace />;
  }

  if (shouldRedirectToIntro) {
    return <Navigate to="/profile/intro" replace />;
  }

  // Always render sidebar wrapper, even during loading
  const content = isLoading ? (
    <div style={LOADING_STYLE}>
      <Spinner animation="grow" variant="info" size="lg" />
    </div>
  ) : (
    children
  );

  return (
    <Wrapper>
      {!shouldExcludeSidebar && <PersistentSidebar blurred={shouldBlurSidebar} />}
      <Container>{content}</Container>
      <WiseClaimRewardPopup />
    </Wrapper>
  );
};

export default IntroScreen;
